import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, ShieldAlert, Sparkles, Building2 } from "lucide-react";
import Navbar from "../components/site/Navbar";
import ContactRail from "../components/site/ContactRail";
import { Footer } from "../components/site/Sections5";
import PageHero from "../components/site/PageHero";
import DedicatedCTA from "../components/site/DedicatedCTA";
import SEO from "../components/site/SEO";
import { getIndustryBySlug, INDUSTRIES } from "../constants/industriesData";
import { getServiceBySlug } from "../constants/servicesData";
import { trackViewContent } from "../lib/metaPixel";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = getIndustryBySlug(slug);

  useEffect(() => {
    if (industry) {
      trackViewContent({
        title: industry.title,
        category: "Industries",
        slug: industry.slug,
      });
    }
  }, [industry]);

  if (!industry) {
    return (
      <div className="min-h-screen bg-navy flex flex-col justify-center items-center text-white px-5 text-center">
        <SEO title="Industry Not Found" />
        <h1 className="font-display text-4xl font-bold uppercase sm:text-6xl">Industry Not Found</h1>
        <p className="mt-4 font-body text-base text-white/70 max-w-md">
          The requested industry page does not exist or may have been updated.
        </p>
        <Link
          to="/industries"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white"
        >
          View All Industries <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const relatedServs = (industry.relatedServices || [])
    .map((sSlug) => getServiceBySlug(sSlug))
    .filter(Boolean);

  const otherIndustries = INDUSTRIES.filter((i) => i.slug !== industry.slug).slice(0, 3);

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid={`industry-detail-${industry.slug}`}>
      <SEO
        title={`${industry.title} Marketing & Production | ADMALL INDIA`}
        description={industry.summary}
        canonical={`https://admallindia.com/industries/${industry.slug}`}
      />
      <Navbar logo="/admall-logo.png" />
      <ContactRail />

      <main className="flex-grow">
        <PageHero
          category="Industries"
          categoryLink="/industries"
          title={industry.title}
          tagline={industry.tagline}
          bgImage={industry.heroImage}
          ctaLabel={`Discuss ${industry.title} Growth`}
          ctaHref="/#contact"
        />

        {/* Strategic Overview */}
        <section className="bg-white py-20 md:py-28 border-b border-navy/10">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <div className="mb-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                  <span>Sector Perspective</span>
                </div>
                <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-5xl">
                  {industry.tagline}
                </h2>
                <p className="mt-6 font-body text-base leading-relaxed text-navy/70 sm:text-lg">
                  {industry.summary}
                </p>
                <div className="mt-8 rounded-2xl bg-[#F8F9FA] p-6 border border-navy/10">
                  <div className="flex items-start gap-3">
                    <Sparkles size={20} className="text-[#FF5722] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-display text-base font-bold uppercase tracking-wider text-navy">
                        Sector Insight
                      </h4>
                      <p className="mt-1 font-body text-sm leading-relaxed text-navy/70">
                        {industry.strategicInsight}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative overflow-hidden rounded-3xl shadow-xl border border-navy/10">
                  <img
                    src={industry.heroImage}
                    alt={industry.title}
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <span className="font-body text-xs font-semibold uppercase tracking-widest text-[#FF5722]">
                      Targeted Solutions
                    </span>
                    <p className="font-display text-xl font-bold uppercase mt-1">
                      {industry.title} Specialized Strategy
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sector Friction Points */}
        {industry.challenges && industry.challenges.length > 0 && (
          <section className="bg-[#F8F9FA] py-20 md:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="max-w-3xl mb-14">
                <div className="mb-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                  <span>Market Friction</span>
                </div>
                <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-navy sm:text-4xl">
                  Key Challenges in {industry.title}
                </h3>
                <p className="mt-4 font-body text-base text-navy/60">
                  Generic agencies misunderstand these sector-specific barriers. We engineer our campaigns specifically to overcome them.
                </p>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                {industry.challenges.map((challenge, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-4 rounded-2xl bg-white p-7 border border-navy/10 shadow-sm"
                  >
                    <ShieldAlert size={22} className="text-[#FF5722] shrink-0 mt-0.5" />
                    <p className="font-body text-base leading-relaxed text-navy/80">
                      {challenge}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Tailored Capabilities & Deliverables */}
        <section className="bg-white py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-3xl mb-14">
              <div className="mb-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                <span>Sector Deliverables</span>
              </div>
              <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-navy sm:text-5xl">
                How We Drive {industry.title} Growth
              </h3>
              <p className="mt-4 font-body text-base text-navy/60">
                Specialized production, distribution, and lead capture built specifically for {industry.title}.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {industry.deliverables.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-navy/10 bg-white p-8 transition hover:border-[#FF5722] hover:shadow-lg flex flex-col justify-between"
                >
                  <div>
                    <span className="h-2 w-2 rounded-full bg-[#FF5722] block mb-4" />
                    <h4 className="font-display text-xl font-bold uppercase tracking-tight text-navy">
                      {item.title}
                    </h4>
                    <p className="mt-3 font-body text-sm leading-relaxed text-navy/60">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Core Services */}
        {relatedServs.length > 0 && (
          <section className="bg-navy py-20 text-white">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                    <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                    <span>Key Capabilities</span>
                  </div>
                  <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                    Disciplines Powering This Sector
                  </h3>
                </div>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:underline"
                >
                  View All Services <ArrowRight size={16} />
                </Link>
              </div>

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {relatedServs.map((serv) => (
                  <Link
                    key={serv.slug}
                    to={`/services/${serv.slug}`}
                    className="group rounded-2xl border border-white/10 bg-white/5 p-7 transition hover:border-[#FF5722] hover:bg-white/10 flex flex-col justify-between"
                  >
                    <div>
                      <h4 className="font-display text-2xl font-bold uppercase tracking-tight text-white group-hover:text-[#FF5722] transition">
                        {serv.title}
                      </h4>
                      <p className="mt-2 font-body text-sm text-white/60 line-clamp-2">
                        {serv.tagline}
                      </p>
                    </div>
                    <span className="mt-6 inline-flex items-center gap-1 font-display text-xs font-bold uppercase tracking-wider text-[#FF5722]">
                      Explore Discipline <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Other Industries Navigation */}
        <section className="bg-[#F8F9FA] py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="mb-10 flex items-center justify-between">
              <div>
                <span className="h-2 w-2 rounded-full bg-[#FF5722] inline-block mb-3 mr-2" />
                <span className="font-body text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  Explore Other Verticals
                </span>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-navy sm:text-3xl mt-1">
                  Additional Industries We Serve
                </h3>
              </div>
              <Link
                to="/industries"
                className="hidden sm:inline-flex items-center gap-1 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722]"
              >
                All Industries <ArrowRight size={16} />
              </Link>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {otherIndustries.map((other) => (
                <Link
                  key={other.slug}
                  to={`/industries/${other.slug}`}
                  className="group rounded-2xl border border-navy/10 bg-white p-7 transition hover:border-[#FF5722] hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <h4 className="font-display text-xl font-bold uppercase tracking-tight text-navy group-hover:text-[#FF5722] transition">
                      {other.title}
                    </h4>
                    <p className="mt-2 font-body text-xs leading-relaxed text-navy/60 line-clamp-2">
                      {other.tagline}
                    </p>
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1 font-display text-xs font-bold uppercase tracking-wider text-[#FF5722]">
                    Explore Vertical <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <DedicatedCTA
          title={`Plan your ${industry.title} marketing with ADMALL`}
          subtitle="Speak with our strategic team in Navi Mumbai. We will evaluate your market catchment and deliver actionable recommendations."
        />
      </main>

      <Footer
        content={{
          logo_url: "/admall-logo.png",
          footer: {
            tagline: "THINK. CREATE. REACH. PERFORM. GROW.",
            disciplines: "Brand Strategy | Creative | Multimedia | Digital Marketing | Advertising | Lead Generation | AI Marketing",
          },
          settings: {
            phone: "+91 93221 06632",
            phone_raw: "+919322106632",
            email: "admallindiagroup@gmail.com",
            location: "Navi Mumbai, Maharashtra, India",
            website: "admallindia.com",
          },
        }}
      />
    </div>
  );
}
